import { parseISO } from 'date-fns';
import { getBucketStart, getBucketStep } from './getTrendPeriod';
import { TrendGroupBy } from './getTrendGroupByOptions';

export interface TrendBucket {
  date: Date;
  allViews: number;
  uniqueViews: number;
}

export interface RawTrendPoint {
  /** ISO date string returned by the API (e.g. "2026-04-01") */
  period: string;
  allViews: number;
  uniqueViews: number;
}

/**
 * Merges raw API data points into a complete, gap-free list of buckets
 * spanning `startObj` → `endObj`.
 *
 * Multiple points that fall into the same UI bucket are summed — this handles
 * the QUARTER mode where the API returns monthly data and the FE aggregates
 * three months per quarter. Note: summing `uniqueViews` across months
 * overcounts repeat visitors; a native BE QUARTER endpoint would fix this.
 *
 * Buckets with no API data are filled with zero values so the chart always
 * renders a complete x-axis.
 */
export default function fillTrendGaps(
  points: RawTrendPoint[],
  startObj: Date,
  endObj: Date,
  groupBy: TrendGroupBy,
): TrendBucket[] {
  // Index raw points by their bucket's start date string so lookups are O(1).
  const byKey = new Map<string, RawTrendPoint>();
  points.forEach((p) => {
    const bucketStart = getBucketStart(parseISO(p.period), groupBy);
    const key = bucketStart.toDateString();
    const existing = byKey.get(key);
    if (existing) {
      byKey.set(key, {
        period: existing.period,
        allViews: existing.allViews + p.allViews,
        uniqueViews: existing.uniqueViews + p.uniqueViews,
      });
    } else {
      byKey.set(key, p);
    }
  });

  const out: TrendBucket[] = [];
  let cursor = getBucketStart(startObj, groupBy);
  let safety = 0;
  // Safety cap prevents an infinite loop if bucket stepping ever stalls
  // (e.g. a future date-fns edge case with unusual timezones).
  while (cursor.getTime() <= endObj.getTime() && safety < 1000) {
    safety += 1;
    const key = cursor.toDateString();
    const found = byKey.get(key);
    out.push({
      date: cursor,
      allViews: found?.allViews ?? 0,
      uniqueViews: found?.uniqueViews ?? 0,
    });
    cursor = getBucketStep(cursor, groupBy, 1);
  }
  return out;
}
